import React from 'react';
import I18n from 'i18n-js';
import { CartBundleExpressApplePayButton } from './CartBundleExpressApplePayButton';
import CheckoutPayPalButton, { isCartEligible } from './CheckoutPayPalButton';
import CartStartCheckoutButton from './CartStartCheckoutButton';
import { CheckoutErrorAlertBox } from './checkout/CheckoutErrorAlertBox';
import {
  core_apimessages_Money as Money,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '../../src/schemas/rql';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useApplePayAvailabilityByConfig } from './useApplePayAvailability';
import { inMobileBrowser } from '@reverbdotcom/commons/src/user_context_helpers';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { PAYPAL_PRESENTMENT_CURRENCIES } from '@reverbdotcom/commons/src/currency';

interface Props {
  paymentMethodType: CheckoutPaymentMethodType;
  checkoutPath: string;
  currency: string;
  checkoutBundlingId: string;
  applePayCheckoutBundlingId: string;
  amountTotal: Money;
  adyenCheckoutPaymentMethodsConfig: string;
  paypalCheckoutExpAttributes: {
    currencyCount: number,
    bundleCount: number,
    shopCount: number,
    isLocalPickupOnly: boolean,
    includesStandalonePaypal: boolean,
  }
}

export default function CheckoutCartFooterButtons(props: Props) {
  const user = useUser();
  const [errors, setErrors] = React.useState([]);

  const [isApplePayAvailable] = useApplePayAvailabilityByConfig(props.adyenCheckoutPaymentMethodsConfig);
  const isDirectCheckout = props.paymentMethodType === CheckoutPaymentMethodType.DIRECT_CHECKOUT;
  const showApplePayButton = isApplePayAvailable && isDirectCheckout;

  const isCartEligibleForPaypalExp = isCartEligible(props.paypalCheckoutExpAttributes);
  const inPaypalButtonRollout = useExpEnabled(experiments.PAYPAL_CHECKOUT_BUTTON);
  const showPaypalButton = inPaypalButtonRollout && isCartEligibleForPaypalExp;

  const inPayPalPresentmentCurrencyRollout = useExpEnabled(experiments.DC_PAYPAL_ALL_THE_PRESENTMENT_CURRENCIES);
  const supportedCurrency = PAYPAL_PRESENTMENT_CURRENCIES.includes(user.currency);
  const paypalCurrency = inPayPalPresentmentCurrencyRollout && supportedCurrency ? user.currency : props.currency;

  const showOne = showApplePayButton !== showPaypalButton;

  const experimentData = {
    componentName: 'CheckoutCartFooterButtons',
    eventName: MParticleEventName.ComponentView,
    experiments: paypalExpValue(),
    ...props.paypalCheckoutExpAttributes,
  };

  function paypalExpValue() {
    if (isCartEligibleForPaypalExp) {
      return formatExperimentsForEventAttributes(user, [experiments.PAYPAL_CHECKOUT_BUTTON]);
    } else {
      return null;
    }
  }

  useViewTracking(experimentData, true);

  return (
    <>
      {errors.length > 0 &&
        <div className="mb-2">
          <CheckoutErrorAlertBox errors={errors} />
        </div>
      }
      <div className="checkout-cart-footer__buttons">
        {(inMobileBrowser(user) || showOne) &&
          <>
            {showPaypalButton &&
              <div className="checkout-cart-footer__button checkout-cart-footer__button--paypal">
                <CheckoutPayPalButton
                  currency={paypalCurrency}
                  checkoutBundlingId={props.checkoutBundlingId}
                  onError={setErrors}
                  variant="cart"
                  parentComponentName="CheckoutCartFooterButtons"
                  listingCurrency={props.currency}
                />
              </div>
            }
            {showApplePayButton &&
              <div className="checkout-cart-footer__button checkout-cart-footer__button--apple-pay">
                <CartBundleExpressApplePayButton
                  variant="white-outlined-express"
                  checkoutBundlingId={props.applePayCheckoutBundlingId}
                  amountTotal={props.amountTotal}
                  adyenCheckoutPaymentMethodsConfig={props.adyenCheckoutPaymentMethodsConfig}
                  tracking={{
                    displayStyle: 'default',
                  }}
                />
              </div>
            }
          </>
        }
        {inPaypalButtonRollout && showOne &&
          <div className="checkout-cart-footer__express-pay-separator">
            {I18n.t('discovery.checkout.expressCheckout.separatorText')}
          </div>
        }
        <CartStartCheckoutButton
          paymentMethodType={props.paymentMethodType}
          checkoutPath={props.checkoutPath}
        />
      </div>
    </>
  );
}
